import React from 'react';
import { Calculator, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет суммы дохода
  const incomeAmount = parseFloat(formData.incomeAmount) || 0;

  // Обработчик изменения суммы дохода
  const handleIncomeAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      incomeAmount: newValue
    }));
  };

  // Обработчик изменения типа дохода
  const handleIncomeTypeChange = (newType) => {
    setFormData(prev => ({
      ...prev,
      incomeType: newType
    }));
  };

  // Обработчик изменения налогового режима
  const handleTaxSystemChange = (newSystem) => {
    setFormData(prev => {
      // Автоматически меняем тип дохода для самозанятых при смене налогового режима
      let newIncomeType = prev.incomeType;
      if (newSystem === 'npd') {
        newIncomeType = prev.fiscalRole === 'entrepreneur' ? 'ip_insurance' : 'general';
      }

      return {
        ...prev,
        taxSystem: newSystem,
        incomeType: newIncomeType
      };
    });
  };

  // Обработчик изменения статуса (физлицо или ИП)
  const handleFiscalRoleChange = (newRole) => {
    setFormData(prev => {
      // Автоматически меняем тип дохода для самозанятых при смене статуса
      let newIncomeType = prev.incomeType;
      if (prev.taxSystem === 'npd') {
        newIncomeType = newRole === 'entrepreneur' ? 'ip_insurance' : 'general';
      }

      return {
        ...prev,
        fiscalRole: newRole,
        hasActiveIP: newRole === 'entrepreneur' ? true : prev.hasActiveIP,
        incomeType: newIncomeType
      };
    });
  };

  // Предопределенные суммы дохода для быстрого выбора
  const predefinedAmounts = [
    { value: '30000', label: '30 тыс.' },
    { value: '100000', label: '100 тыс.' },
    { value: '300000', label: '300 тыс.' },
    { value: '1000000', label: '1 млн' }
  ];

  // Функция расчета
  const calculateTax = () => {
    document.querySelector('button[type="submit"]')?.click();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый расчет налогов
      </h3>
      
      <div className="space-y-5">
        {/* Налоговый статус */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Ваш статус</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleFiscalRoleChange('individual')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.fiscalRole === 'individual' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Физическое лицо</div>
              <div className="text-xs opacity-75">Самозанятый</div>
            </button>
            <button
              type="button"
              onClick={() => handleFiscalRoleChange('entrepreneur')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.fiscalRole === 'entrepreneur' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">ИП</div>
              <div className="text-xs opacity-75">Предприниматель</div>
            </button>
          </div>
        </div>

        {/* Налоговый режим */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Налоговый режим</div>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => handleTaxSystemChange('npd')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.taxSystem === 'npd' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Самозанятость (НПД)</div>
              <div className="text-xs opacity-75">Ставка 4-6%</div>
            </button>
            <button
              type="button"
              onClick={() => handleTaxSystemChange('usn_income')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.taxSystem === 'usn_income' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">УСН Доходы</div>
              <div className="text-xs opacity-75">Ставка 6%</div>
            </button>
            <button
              type="button"
              onClick={() => handleTaxSystemChange('usn_profit')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.taxSystem === 'usn_profit' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">УСН Доходы-Расходы</div>
              <div className="text-xs opacity-75">Ставка 15%</div>
            </button>
          </div>
        </div>
        
        {/* Сумма дохода */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="incomeAmountSlider" className="text-gray-600 dark:text-gray-400">Доход за период</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(incomeAmount, 0)}</span>
          </div>
          <input
            type="range"
            id="incomeAmountSlider"
            min="10000"
            max="1000000"
            step="10000"
            value={incomeAmount}
            onChange={handleIncomeAmountChange}
            aria-label="Сумма дохода"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, incomeAmount: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.incomeAmount === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип дохода для самозанятых */}
        {formData.taxSystem === 'npd' && (
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Источник дохода</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleIncomeTypeChange('general')}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.incomeType === 'general' 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium">От физлиц</div>
                <div className="text-xs opacity-75">Ставка 4%</div>
              </button>
              <button
                type="button"
                onClick={() => handleIncomeTypeChange('ip_insurance')}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.incomeType === 'ip_insurance' 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium">От ИП/организаций</div>
                <div className="text-xs opacity-75">Ставка 6%</div>
              </button>
            </div>
          </div>
        )}
        
        {/* Включить расходы (для УСН Доходы-Расходы) */}
        {formData.taxSystem === 'usn_profit' && (
          <div>
            <div className="flex justify-between items-center mb-1 text-sm">
              <label className="text-gray-600 dark:text-gray-400">Учесть расходы</label>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  hasExpenses: !prev.hasExpenses,
                  expenses: prev.hasExpenses ? '0' : (0.6 * parseFloat(prev.incomeAmount)).toString()
                }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.hasExpenses 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {formData.hasExpenses ? 'Включено' : 'Выключено'}
              </button>
            </div>
            {formData.hasExpenses && (
              <div className="mt-2">
                <input
                  type="text"
                  value={formData.expenses}
                  onChange={(e) => setFormData(prev => ({ ...prev, expenses: e.target.value }))}
                  className="form-input text-sm p-1.5 text-center"
                  placeholder="Сумма расходов"
                />
                <div className="mt-1 grid grid-cols-3 gap-1">
                  {[0.3, 0.5, 0.7].map(ratio => (
                    <button
                      key={ratio}
                      type="button"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        expenses: Math.round(parseFloat(prev.incomeAmount) * ratio).toString() 
                      }))}
                      className="px-2 py-1 text-xs bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      {ratio * 100}% от дохода
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Включить страховые взносы для ИП */}
        {formData.fiscalRole === 'entrepreneur' && (
          <div>
            <div className="flex justify-between items-center mb-1 text-sm">
              <label className="text-gray-600 dark:text-gray-400">Фиксированные взносы ИП</label>
              <span className="font-medium text-indigo-600 dark:text-indigo-400">45 842 ₽</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Учитываются автоматически для всех ИП за 2023 год
            </div>
          </div>
        )}
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTax}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать налоги
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;